import * as BABYLON from 'babylonjs';
import Utils3D from 'shared/libraries/mwc/3d/utils/utils3D';

// helper function
function calcGroupIndex(s,x) {
    let idx;
    if (s.length === 0) {
        return 0;
    }
    for (idx = 0; idx <= s.length; idx++) {
        let a = (idx === 0) ? -Infinity : s[idx - 1];
        let b = (idx === s.length) ? Infinity : s[idx];
        if (x >= a && x < b) {
            break;
        }
    }
    return idx;
}

export default class NonLinearRescaler {

    // Arguments:
    //  - mesh: Babylon js mesh instance
    constructor(mesh) {
        this.debug = false;
        this.naive = false;

        this.splits = [[],[],[]];
        this.wallPlanes = [[],[],[]];

        mesh.makeGeometryUnique();

        this.mesh = mesh;
        this.data = mesh.getVerticesData(BABYLON.VertexBuffer.PositionKind);
        this.meshTransform = this.mesh.getWorldMatrix();
        this.meshTransformInverse = new BABYLON.Matrix();
        this.meshTransform.invertToRef(this.meshTransformInverse);

        this.buffer = this.data.slice(0);

        this._computeCenterAndDims();
        this._groupVertices();

        this.redundantPlanes = false; // Which means we have even group without vertices
        this.redundantPlanesAxes = [];
        this.redundantPlanesGroupIds = [];
    }

    sync() {
        let bIsUpdatable = this.mesh.geometry.getVertexBuffer(BABYLON.VertexBuffer.PositionKind).isUpdatable();
        if(!bIsUpdatable)
        {
            // Setting vertex data deletes submeshes; however, we cannot call update only, because
            // initially the mesh is loaded as non-updatable (and this is hard-coded into the
            // Babylon file loader, unfortunately). So instead, we set the vertices the first time,
            // telling the mesh to be updatable, and after that, we only make the update call.
            // Credit to Mert for finding out a solution to preserve submeshes.
            let subMeshesBackup = this.mesh.subMeshes.slice();
            this.mesh.setVerticesData(BABYLON.VertexBuffer.PositionKind, this.buffer, true);
            Utils3D.restoreSubmeshes(subMeshesBackup, this.mesh);
        }
        else
        {
            this.mesh.updateVerticesData(BABYLON.VertexBuffer.PositionKind, this.buffer, true, false);
            this.mesh.computeWorldMatrix(true);
        }
    }

    // reset model to original state
    reset() {
        for(let i=0; i<3; i++) {
            this._resetDim(i);
        }
        this.sync();
    }

    stash() {
        this.stashedBuffer = this.buffer.slice(0);
        this.reset();
    }

    unstash() {
        this.buffer = this.stashedBuffer;
        this.stashedBuffer = null;
        this.sync();
    }

    // Argument:
    //  - mode: 'l' (length), 's' (scale)
    //  - arg: if mode is 'l', arg is a 3-element vector containing desired lengths in the X, Y and Z directions
    //         if mode is 's', arg is a 3-element vector containing scale factors along the X, Y and Z directions
    // Nonlinear scaling is applied by default. To use naive scaling, set the naive property to false
    rescale(mode,arg) {
        let sf = arg.slice();
        if(mode === 'l') {
            for(let i = 0; i < 3; i++) {
                sf[i] = (this.wallPlanes[i].length === 0)? sf[i]/this.dims[i]:sf[i]/this.dimsWall[i];
            }
        }
        for (let j = 0; j < 3; j++) {
            if (sf[j] !== 1) {
                if (this.naive || (this.splits[j].length === 0 && this.wallPlanes[j].length === 0)) {
                    this._rescaleNaive(sf[j],j);
                } else {
                    this._rescaleNonLin(sf[j],j);
                }
            } else {
                this._resetDim(j);
            }
        }
        this.sync();
    }

    // Set split planes
    // Required: an even number of split planes per dimension and plane positions must be sorted in ascending order
    //
    // [
    //   [<split plane #1 X-pos>, ..., <split plane #N1 X-pos>]
    //   [<split plane #1 Y-pos>, ..., <split plane #N2 Y-pos>]
    //   [<split plane #1 Z-pos>, ..., <split plane #N3 Z-pos>]
    // ]
    // E.g.
    //  [
    //      [-200,300],
    //      [],
    //      [100,200,300,400]
    //  ]
    //
    setSplitsRaw(splits) {
        let ok = true;

        for (let j = 0; j < splits.length; j++) {
            let x = splits[j];
            if (x.length % 2 === 1) {
                console.error('An even number of split planes is required');
                ok = false;
                break;
            }
            if (!x.every((split,i) => (i === 0) || (x[i] > x[i - 1]))) {
                console.error('Split plane positions must be in ascending order');
                ok = false;
                break;
            }
        }
        if (ok) {
            this.splits = splits;
        } else {
            this.splits = [[],[],[]];
        }
        this._groupVertices();
    }


    convertLocalToWorld(splitsOrWalls) {
        let newArray = [[],[],[]];

        for(let i = 0; i < 3; i++) {
            newArray[i] = splitsOrWalls[i].slice();
            for (let j = 0; j < splitsOrWalls[i].length; j++) {
                newArray[i][j] = splitsOrWalls[i][j] + this.center[i];
            }
        }

        return newArray;
    }

    // 3dsmax wants this coordinate system
    convertWorldToLocal(splitsOrWalls) {
        let newArray = [[],[],[]];

        for(let i = 0; i < 3; i++) {
            newArray[i] = splitsOrWalls[i].slice();
            for (let j = 0; j < splitsOrWalls[i].length; j++) {
                newArray[i][j] = splitsOrWalls[i][j] - this.center[i];
            }
        }

        return newArray;
    }

    // Incoming splits are BBox centered
    setSplits(splits) {
        this.setSplitsRaw(this.convertLocalToWorld(splits));
    }

    // Set Wall planes
    // Required: array consists of 3 array that can have 2 number or none.
    // E.g.
    //  [
    //      [-200,300],
    //      [],
    //      [100,200]
    //  ]
    //
    setWallPlanesRaw(wallPlanes) {
        let ok = true;

        if(wallPlanes.length !== 3) {
            console.error('wall planes should have 3 axis');
            ok = false;
        }
        else{
            for (let j = 0; j < wallPlanes.length; j++) {
                let x = wallPlanes[j];
                if (x.length !== 2 && x.length !== 0) {
                    console.error('Only accepted 2 items or none');
                    ok = false;
                    break;
                }
            }
        }

        this.wallPlanes = (ok)? wallPlanes: [[],[],[]];
        this._groupVertices();
    }

    // Incoming splits are BBox centered
    setWallPlanes(wallPlanes) {
        this.setWallPlanesRaw(this.convertLocalToWorld(wallPlanes));
    }


    /*
    This takes in splits as a percentage relative to the center of the mesh.
    So, for example, a split for the two outermost 10% sections of the mesh
    would be set as [-10, 10].
    */
    setPercentageSplits(splits)
    {
        let absSplits = [];
        for(let i = 0; i < 3; i++)
        {
            absSplits[i] = [];
            for(let iSplitIndex = 0; iSplitIndex < splits[i].length; iSplitIndex++)
            {
                let fDist = splits[i][iSplitIndex] / 100.0 * this.dims[i] + this.bbmin[i];
                absSplits[i].push(fDist);
            }
        }

        this.setSplits(absSplits);
    }

      /////////////////////
     // Private methods //
    /////////////////////

    /** Compute scaled position.
     * @param axis Axis on which the scaling is performed.
     * @param scale Scale factor.
     * @param p 3D point.
     * @param inputMode Whether the 3D point is expressed in local or world coordinate system.
     * @param outputMode Whether the resulting 3D point should be expressed in local or world coordinate system.
     * @return {*} Scaled 3D point.
     */
    computeScaledPosition(axis, scale, p, inputMode, outputMode) {
        if (scale <= 1e-2) {
            return null;
        }
        let pLocal = p;
        if (!inputMode) {
            pLocal = BABYLON.Vector3.TransformCoordinates(p, this.meshTransformInverse);
        }
        let pLocalScaled = null;
        if (
            this.naive
            || (
                (this.splits[axis].length === 0)
                && (this.wallPlanes[axis].length === 0)
            )
        ) {
            switch (axis) {
                case 0: {
                    pLocalScaled = pLocal.multiplyByFloats(scale, 1.0, 1.0);
                    break;
                }
                case 1: {
                    pLocalScaled = pLocal.multiplyByFloats(1.0, scale, 1.0);
                    break;
                }
                case 2: {
                    pLocalScaled = pLocal.multiplyByFloats(1.0, 1.0, scale);
                    break;
                }
                default: {
                    break;
                }
            }
        } else {
            let useWallPlanes = (this.wallGroups[axis].length !== 0);
            const parameters = this.computeNonLinearScalingParameters(scale, axis, useWallPlanes);
            const originalValue = (axis === 0) ? pLocal.x : ((axis === 1) ? pLocal.y : pLocal.z);
            let groupIndex = this.computeNonLinearScalingGroupIndex(parameters, originalValue);
            if (groupIndex >= 0) {
                const scaledValue = this.computeNonLinearScaledValue(parameters, groupIndex, originalValue);
                pLocalScaled = new BABYLON.Vector3(
                    (axis === 0) ? scaledValue : pLocal.x,
                    (axis === 1) ? scaledValue : pLocal.y,
                    (axis === 2) ? scaledValue : pLocal.z
                );
            }
        }
        if (pLocalScaled !== null) {
            if (outputMode) {
                return pLocalScaled;
            } else {
                const pGlobalScaled = BABYLON.Vector3.TransformCoordinates(pLocalScaled, this.meshTransform);
                return pGlobalScaled;
            }
        }
        return null;
    }

    /** Compute scaled position.
     * @param scaling Scale vector.
     * @param p 3D point.
     * @param inputMode Whether the input 3D point is expressed in local or world coordinate system.
     * @param outputMode Whether the resulting 3D point should be expressed in local or world coordinate system.
     * @return {*} Scaled 3D point.
     */
    computeScaledPositionFromVector(scaling, p, inputMode, outputMode) {
        const scaledPositions = [];
        [0, 1, 2].forEach((axis) => {
            const scale = (axis === 0) ? scaling.x : ((axis === 1) ? scaling.y : scaling.z);
            const localScaled = this.computeScaledPosition(
                axis,
                scale,
                p,
                inputMode,
                outputMode
            );
            scaledPositions.push(localScaled);
        })
        const pScaled = BABYLON.Vector3.Zero();
        if (scaledPositions[0] !== null) {
            pScaled.x = scaledPositions[0].x;
        }
        if (scaledPositions[1] !== null) {
            pScaled.y = scaledPositions[1].y;
        }
        if (scaledPositions[2] !== null) {
            pScaled.z = scaledPositions[2].z;
        }
        return pScaled;
    }

    _resetDim(axis) {
        for (let i = axis; i < this.data.length; i += 3) {
            this.buffer[i] = this.data[i];
        }

        this.bbminWallTotalAfterScale[axis] = Math.min(this.bbminWall[axis], this.bbmin[axis]);
        this.bbmaxWallTotalAfterScale[axis] = Math.max(this.bbmaxWall[axis], this.bbmax[axis]);
    }

    _rescaleNaive(scaleFactor,axis) {
        for (let i = axis; i < this.data.length; i += 3) {
            this.buffer[i] = this.center[axis] + scaleFactor*(this.data[i] - this.center[axis]);
        }
    }

    // Nonlinear scaling
    //
    // Algorithm sketch based on 5 groups
    // Each group may or may not contain vertices
    //
    //
    //                  L
    //        |<--------------------->|
    //        |     |    |   |   |    |
    //        |  a  |  x | b | y | c  |
    //        +=====+----+===+---+====+
    //              |    |   |   |
    // group      0 |  1 | 2 | 3 | 4
    //              |    |   |   |
    // splits       0    1   2   3
    //
    // Original length L = a + b + c + x + y
    //
    // a,b,c are fixed (rigid parts)
    // x,y   are variable (stretchable parts)
    //
    // even groups are rigid
    // odd groups are stretchable
    //
    // number of splits should always be even
    //
    // Let L2 be desired length.
    // We need to find scaling factors alpha and beta that
    // scale the rigid and stretchable parts, resp.
    // Alpha and beta must meet the following condition:
    //      L2 = alpha*(a + b + c) + beta*(x + y)
    // ==> alpha = 1
    // ==> beta = max(0, (L2 - (a + b + c)) / (x + y))
    //
    // Beta has to be strictly positive, otherwise we will get
    // degenerate geometry.
    // We ensure that beta >= epsilon. Now scale factor alpha has
    // to be decreased to ensure the target length L2 is still
    // achievable:
    //      if L2 - (a + b + c) > epsilon
    //          alpha = 1
    //          beta = (L2 - (a + b + c)) / (x + y)
    //      otherwise
    //          alpha = (L2 - epsilon*(x + y)) / (a + b + c)
    //          beta = epsilon
    //
    //
    // To reconstruct the vertices va, vx, vb, vy, vc of groups a,x,b,y,c, resp.:
    // va := alpha*(va - groupstart(a)) - L2/2 + center
    // vx := alpha*a + beta*(vx - groupstart(x)) - L2/2 + center
    // vb := alpha*a + beta*x + alpha*(vb - groupstart(b)) - L2/2 + center
    // vy := alpha*a + beta*x + alpha*b beta*(vy - groupstart(y)) - L2/2 + center
    // vc := alpha*a + beta*x + alpha*b + beta*y + alpha*(vc - groupstart(c)) - L2/2 + center
    // where groupstart(a) is the minimum coordinate in group a, etc.
    //
    // We can rewrite this with affine transformations of the form "u*x + v".
    // Note that u is equal to alpha for a,b,c (even groups) and equal to beta for x,y (odd groups)
    // The v-values are:
    // - for group a, v = 0 +                                   - alpha * groupstart(a) - L2/2 + center
    // - for group x, v = alpha*a                               - beta  * groupstart(x) - L2/2 + center
    // - for group b, v = alpha*a + beta*x                      - alpha * groupstart(b) - L2/2 + center
    // - for group y, v = alpha*a + beta*x + alpha*b            - beta  * groupstart(y) - L2/2 + center
    // - for group c, v = alpha*a + beta*x + alpha*b + beta*y + - alpha * groupstart(c) - L2/2 + center
    //
    _rescaleNonLin(scaleFactor, axis) {
        let groupIdx = this.groupIdx;
        let data = this.data;
        let buffer = this.buffer;

        let useWallPlanes = this.wallGroups[axis].length !== 0;

        const parameters = this.computeNonLinearScalingParameters(scaleFactor, axis, useWallPlanes);
        if (!useWallPlanes) {
            // apply affine transformation to each vertex coordinate
            for (let i = axis; i < data.length; i += 3) {
                let k = groupIdx[i];
                const value = data[i];
                buffer[i] = this.computeNonLinearScaledValue(parameters, k, value);
            }
        } else {
            if (parameters.length < 10) {
                console.log("Issue with parameters");
                return;
            }

            let G = parameters[7];
            let offsets = parameters[8];
            let newCenter = parameters[9];
            let n = G.length;

            // apply affine transformation to each vertex coordinate
            for (let k = 0; k < n; k++) {
                for(let i = 0; i < G[k].indices.length; i++) {
                    let targetIndex = G[k].indices[i];
                    const value = data[targetIndex];
                    buffer[targetIndex] = this.computeNonLinearScaledValue(parameters, k, value);
                }
            }

            this.bbminWallTotalAfterScale[axis] = 0 - newCenter + this.centerWall[axis];
            this.bbmaxWallTotalAfterScale[axis] = offsets[n-1] - newCenter + this.centerWall[axis];
        }
    }

    /** Compute Non-Linear Scaling parameters, see _rescaleNonLin.
     * @param scaleFactor
     * @param axis
     * @param useWallPlanes
     * @return {*[]} Array of parameters, as explained in _rescaleNonLin. It should contain exactly 10 elements.
     */
    computeNonLinearScalingParameters(scaleFactor, axis, useWallPlanes) {
        let group = this.group;
        let center = this.center;

        if (!useWallPlanes) {
            let sumRigid = this.sumRigid[axis];
            let sumStretch = this.sumStretch[axis];

            // original dimension L
            let L = sumRigid + sumStretch;

            // desired dimension L2
            let L2 = scaleFactor * L;

            // compute scaling factors alpha and beta
            let alpha, beta;
            const epsilon = 1e-3;
            if (L2 - sumRigid > epsilon) {
                alpha = 1;
                beta = (L2 - sumRigid) / sumStretch;
            } else {
                alpha = (L2 - epsilon*sumStretch) / sumRigid;
                beta = epsilon;
            }
            if (this.debug) {
                console.log('alpha = ' + alpha.toFixed(3) + ' beta = ' + beta.toFixed(3));
            }

            // compute offsets
            let G = group[axis];
            let n = G.length;
            let offsets = Array(n);
            let tmp = 0;
            for (let k = 0; k < n; k++) {
                let u = (k % 2 === 0) ? alpha : beta;
                offsets[k] = tmp - u * G[k].min - L2/2 + center[axis];
                if (k < n - 1) {
                    tmp += u * G[k].len;
                }
            }

            return [scaleFactor, axis, useWallPlanes, L, L2, alpha, beta, G, offsets, null];
        } else {
            let sumRigid = this.wallInnerSumRigid[axis];
            let sumStretch = this.wallInnerSumStretch[axis];

            // original dimension L
            let L = sumRigid + sumStretch;

            // desired dimension L2
            let L2 = scaleFactor * L;

            // compute scaling factors alpha and beta
            let alpha, beta;
            const epsilon = 1e-3;
            if (L2 - sumRigid > epsilon && sumStretch !== 0) {
                alpha = 1;
                beta = (L2 - sumRigid) / sumStretch;
            } else {
                alpha = (L2 - epsilon * sumStretch) / sumRigid;
                beta = epsilon;
            }
            if (this.debug) {
                console.log('alpha = ' + alpha.toFixed(3) + ' beta = ' + beta.toFixed(3));
            }

            // compute offsets
            let G = this.wallGroups[axis];
            let n = G.length;
            let offsets = Array(n);
            for (let k = 0; k < n; k++) {
                let u = (G[k].type === 0) ? alpha : beta;
                let before = (k === 0) ? 0 : offsets[k-1];
                offsets[k] = before + u * G[k].len;
            }

            // Decide to wall boundaries to calculate new center
            let start = 0;
            let end = offsets[n-1];
            for (let k = 0; k < n; k++) {
                if(k !== 0 && G[k-1].area === 0 && G[k].area === 1) {
                    start = offsets[k-1];
                }
                if(k !== n-1 && G[k].area === 1 && G[k+1].area === 2) {
                    end = offsets[k];
                }
            }
            let newCenter = (end + start) / 2.0;

            return [scaleFactor, axis, useWallPlanes, L, L2, alpha, beta, G, offsets, newCenter];
        }
    }

    computeNonLinearScalingGroupIndex(parameters, value) {
        if (
            (parameters === null)
            || (parameters.length < 10)
        ) {
            console.log("Issue with parameters");
            return Number.MIN_VALUE;
        }

        let axis = parameters[1];
        let useWallPlanes = parameters[2];

        let groupIndex = -1;
        if (!useWallPlanes) {
            groupIndex = calcGroupIndex(this.splits[axis], value);
        } else {
            for(let k = 0; k < this.wallGroups[axis].length; k++) {
                let g = this.wallGroups[axis][k];
                if(
                    (g.min <= value)
                    && (value <= g.max)
                ) {
                    groupIndex = k;
                    break;
                }
            }
        }

        return groupIndex;
    }

    computeNonLinearScaledValue(parameters, groupIndex, value) {
        if (
            (parameters === null)
            || (parameters.length < 10)
        ) {
            console.log("Issue with parameters");
            return Number.MIN_VALUE;
        }

        let axis = parameters[1];
        let useWallPlanes = parameters[2];
        let alpha = parameters[5];
        let beta = parameters[6];
        let G = parameters[7];
        let offsets = parameters[8];
        let newCenter = parameters[9];

        if (!useWallPlanes) {
            let u = (groupIndex % 2 === 0) ? alpha : beta;
            let v = offsets[groupIndex];
            let scaledValue = u * value + v;
            return scaledValue;
        } else {
            let u = (G[groupIndex].type === 0) ? alpha : beta;
            let offset = (groupIndex === 0) ? 0 : offsets[groupIndex - 1];
            let minimum = G[groupIndex].min;
            let scaledValue = offset + u*(value - minimum) - newCenter + this.centerWall[axis];
            return scaledValue;
        }
    }

    computeNonLinearOriginalValue(parameters, groupIndex, scaledValue) {
        if (
            (parameters === null)
            || (parameters.length < 10)
        ) {
            console.log("Issue with parameters");
            return Number.MIN_VALUE;
        }

        let axis = parameters[1];
        let useWallPlanes = parameters[2];
        let alpha = parameters[5];
        let beta = parameters[6];
        let G = parameters[7];
        let offsets = parameters[8];
        let newCenter = parameters[9];

        if (!useWallPlanes) {
            let u = (groupIndex % 2 === 0) ? alpha : beta;
            let v = offsets[groupIndex];
            let originalValue = (scaledValue - v) / u;
            return originalValue;
        } else {
            let u = (G[groupIndex].type === 0) ? alpha : beta;
            let offset = (groupIndex === 0) ? 0 : offsets[groupIndex - 1];
            let minimum = G[groupIndex].min;
            let originalValue = minimum + (scaledValue - offset + newCenter - this.centerWall[axis]) / u;
            return originalValue;
        }
    }

    _computeCenterAndDims() {
        let x = this.data;
        let bbmin = [x[0],x[1],x[2]];
        let bbmax = [x[0],x[1],x[2]];
        for (let i = 1; i < x.length; i++) {
            let j = i % 3;
            bbmin[j] = (x[i] < bbmin[j]) ? x[i] : bbmin[j];
            bbmax[j] = (x[i] > bbmax[j]) ? x[i] : bbmax[j];
        }
        this.bbmin = bbmin;
        this.bbmax = bbmax;
        this.dims = [0,1,2].map(i => bbmax[i] - bbmin[i]);
        this.center = [0,1,2].map(i => (bbmin[i] + bbmax[i]) / 2);
    }

    _groupVertices() {
        let data = this.data;
        let splits = this.splits;

        // reset redundants
        this.redundantPlanes = false;
        this.redundantPlanesAxes = [];
        this.redundantPlanesGroupIds = [];

        // create groups based on splits
        let group = Array(3);
        for (let j = 0; j < 3; j++) {
            group[j] = [];
            let nSplits = this.splits[j].length;
            for (let k = 0; k <= nSplits; k++) {
                group[j].push({
                    indices: [],
                    min: 0,
                    max: 0,
                    len: 0
                });
            }
        }
        // classify each coordinate
        let groupIdx = Array(data.length);
        for (let j = 0; j < 3; j++) {
            for (let i = j; i < data.length; i += 3) {
                let idx = calcGroupIndex(this.splits[j], data[i]);
                group[j][idx].indices.push(i);
                groupIdx[i] = idx;
            }
        }

        let sumRigid = [0, 0, 0];
        let sumStretch = [0, 0, 0];
        for (let j = 0; j < 3; j++) {
            // compute min and max coordinates per group
            let n = group[j].length;
            for (let k = 0; k < n; k++) {
                let g = group[j][k];
                let x = g.indices.map(i => data[i]);
                if (x.length > 0) {
                    g.min = Math.min.apply(null, x);
                    g.max = Math.max.apply(null, x);
                    g.len = g.max - g.min;
                } else {
                    // empty group
                    g.len = NaN;
                    if (n > 1 && k % 2 === 0) {
                        console.warn('Even group without vertices: axis ' + (j + 1) + ' group ' + (k + 1) + ' of ' + n);
                        g.len = 0;
                        this.redundantPlanes = true;
                        this.redundantPlanesAxes.push(j);
                        this.redundantPlanesGroupIds.push(k);
                    }
                }
            }
            // expand stretchable groups
            for (let k = 1; k < n; k += 2) {
                let g = group[j][k];
                g.min = group[j][k - 1].max;
                g.max = group[j][k + 1].min;
                g.len = g.max - g.min;
                sumStretch[j] += g.len;
            }

            for (let k = 0; k < n; k += 2) {
                sumRigid[j] += group[j][k].len;
            }
        }

        this.group = group;
        this.groupIdx = groupIdx;
        this.sumRigid = sumRigid;
        this.sumStretch = sumStretch;

        // +-----------------------------+
        // |                             |
        // |         Wall Planes         |
        // |                             |
        // +-----------------------------+
        // Calculating groups in Wall Planes
        let wallGroups = [[],[],[]];
        let wallSumRigid = [0,0,0];
        let wallSumStretch = [0,0,0];

        // Detect groups positions
        //              wall planes
        //        min                 max
        //  area 0 |      area 1       | area 2
        let detectArea = (p, minP, maxP) => {
            if(p < minP) {
                return 0;
            }else if(p < maxP) {
                return 1;
            }
            return 2;
        };

        // Calculate inner groups
        for (let axis = 0; axis < 3; axis++) {
            if(this.wallPlanes[axis].length === 0) {
                continue;
            }

            let wallMin = this.wallPlanes[axis][0];
            let wallMax = this.wallPlanes[axis][1];

            // Check minimum is out of fixture's bb
            //  min                         max
            //   +    bbmin         bbmax    +
            //   | ???? +-------------+      |
            //   | here |             |      |
            //   | ???? +-------------+      |
            //   +                           +
            if(this.bbmin[axis] > wallMin) {
                wallGroups[axis].push({ area: 1,
                                        type: 2,
                                        min: wallMin,
                                        max: this.bbmin[axis],
                                        indices: [],
                                        len: this.bbmin[axis] - wallMin});
            }


            for(let k = 0; k < group[axis].length; k++) {
                let g = group[axis][k];
                let type = (k % 2 === 0)? 0 : 1;
                let minArea = detectArea(g.min, wallMin, wallMax);
                let maxArea = detectArea(g.max, wallMin, wallMax);

                // Check they are in same area
                // No need to divide so add directly and continue
                if(minArea === maxArea) {
                    wallGroups[axis].push({
                        area: minArea,
                        type: type,
                        min: g.min,
                        max: g.max,
                        indices: [],
                        len: g.len});
                    continue;
                }

                // they are NOT in same area
                // So we have to divide this into groups
                if(minArea === 0) {
                    wallGroups[axis].push({
                        area: 0,
                        type: type,
                        min: g.min,
                        max: wallMin,
                        indices: [],
                        len: wallMin - g.min});
                }
                else{
                    wallGroups[axis].push({
                        area: 1,
                        type: type,
                        min: g.min,
                        max: wallMax,
                        indices: [],
                        len: wallMax - g.min});
                }

                if(minArea === 0 && maxArea === 2) {
                    wallGroups[axis].push({
                        area: 1,
                        type: type,
                        min: wallMin,
                        max: wallMax,
                        indices: [],
                        len: wallMax - wallMin});
                }

                if(maxArea === 1) {
                    wallGroups[axis].push({
                        area: 1,
                        type: type,
                        min: wallMin,
                        max: g.max,
                        indices: [],
                        len: g.max - wallMin});
                }
                else{
                    wallGroups[axis].push({
                        area: 2,
                        type: type,
                        min: wallMax,
                        max: g.max,
                        indices: [],
                        len: g.max - wallMax});
                }

            }

            // Check maximum is out of fixture's bb
            //  min                         max
            //   +    bbmin         bbmax    +
            //   |      +-------------+ ???? |
            //   |      |             | here |
            //   |      +-------------+ ???? |
            //   +                           +
            if(this.bbmax[axis] < wallMax) {
                wallGroups[axis].push({ area: 1,
                                        type: 2,
                                        min: this.bbmax[axis],
                                        max: wallMax,
                                        indices: [],
                                        len: wallMax - this.bbmax[axis]});
            }

        }

        // Recalculate sum rigid and stretch in wall planes
        for (let axis = 0; axis < 3; axis++) {
            for( let k = 0; k < wallGroups[axis].length; k++) {
                let g = wallGroups[axis][k];
                if(g.area !== 1) {
                    continue;
                }

                if(g.type === 0) {
                    wallSumRigid[axis] += g.len;
                }
                else{
                    wallSumStretch[axis] += g.len;
                }
            }
        }

        // calculate vertices wall groups
        for(let axis = 0; axis < 3; axis++) {
            for(let k = 0; k < wallGroups[axis].length; k++) {
                let g = wallGroups[axis][k];

                for(let i = axis; i < data.length; i += 3) {
                    if(g.min <= data[i] && data[i] <= g.max ) {
                        g.indices.push(i);
                    }
                }
            }
        }

        this.wallGroups = wallGroups;
        this.wallInnerSumRigid = wallSumRigid;
        this.wallInnerSumStretch = wallSumStretch;

        this.bbminWall = [0,1,2].map(i => { return (this.wallPlanes[i].length===0)?this.bbmin[i]:this.wallPlanes[i][0]; });
        this.bbmaxWall = [0,1,2].map(i => { return (this.wallPlanes[i].length===0)?this.bbmax[i]:this.wallPlanes[i][1]; });
        this.dimsWall = [0,1,2].map(i => { return (this.wallPlanes[i].length===0)?this.dims[i]:this.wallPlanes[i][1] - this.wallPlanes[i][0]; });
        this.centerWall = [0,1,2].map(i => { return (this.wallPlanes[i].length===0)?this.center[i]:(this.wallPlanes[i][1] + this.wallPlanes[i][0]) / 2.0; });

        this.bbminWallTotalAfterScale = [0,1,2].map(i => { return Math.min(this.bbminWall[i], this.bbmin[i]); });
        this.bbmaxWallTotalAfterScale = [0,1,2].map(i => { return Math.max(this.bbmaxWall[i], this.bbmax[i]); });
    }

    isRedundant(axis, groupId) {
        for(let i=0; i<this.redundantPlanesAxes.length; i++) {
            if(this.redundantPlanesAxes[i] === axis && this.redundantPlanesGroupIds[i] === groupId)
                return true;
        }
        return false;
    }

    getWallDim() {
        return this.dimsWall;
    }

    getWallCenter() {
        return this.centerWall;
    }

    isWallAvailable(axis) {
        return this.wallPlanes[axis].length !== 0;
    }

    static setupNonLinearScalingForBabylonMesh(fixtureMesh) {
        if (!fixtureMesh.nlrs) {
            fixtureMesh.nlrs = new NonLinearRescaler(fixtureMesh);

            const splitPlanes = [[], [], []];
            const wallPlanes = [[], [], []];

            if (!fixtureMesh.extraModelInfo) {
                window.reportError(
                    'setupNonLinearScalingForBabylonMesh',
                    `Fixture model error: 'extraModelInfo' not valid, id= ${fixtureMesh.fixtureId}`
                );
            } else if (!fixtureMesh.extraModelInfo.data) {
                /**
                 * This just requires a warning. The absence of the data field just means that
                 * the split/wall planes haven't been set, which is not a strict requirement
                 * for a fixture to function.
                 */
                console.warn(`Fixture with id= ${fixtureMesh.fixtureId} does not have any split or wall planes`);
            } else {
                if (fixtureMesh.extraModelInfo.data.splitting_planes) {
                    splitPlanes[0] = fixtureMesh.extraModelInfo.data.splitting_planes.x.slice();
                    splitPlanes[1] = fixtureMesh.extraModelInfo.data.splitting_planes.z.slice();
                    splitPlanes[2] = fixtureMesh.extraModelInfo.data.splitting_planes.y.slice();
                }
                if (fixtureMesh.extraModelInfo.data.wall_planes) {
                    wallPlanes[0] = fixtureMesh.extraModelInfo.data.wall_planes.x.slice();
                    wallPlanes[1] = fixtureMesh.extraModelInfo.data.wall_planes.z.slice();
                    wallPlanes[2] = fixtureMesh.extraModelInfo.data.wall_planes.y.slice();
                }
            }

            fixtureMesh.nlrs.setSplits(splitPlanes);
            fixtureMesh.nlrs.setWallPlanes(wallPlanes);
        }
        return fixtureMesh.nlrs;
    }   
}
